/**
 * DateCell Component
 * 
 * Renders Parse Date values with:
 * - Human-readable format: "27 Nov 2025 15:44"
 * - ISO string in tooltip
 * - Support for both ISO strings and Parse Date objects
 */

import { CellRendererProps, ParseDate } from '../../../../../types/AppPanel';

type DateValue = string | Date | ParseDate | null | undefined;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function DateCell({ 
  value 
}: CellRendererProps<DateValue>) {
  // Handle null/undefined
  if (value === null) {
    return <span className="text-white/30 italic">(null)</span>;
  }
  if (value === undefined) {
    return <span className="text-white/30 italic">(undefined)</span>;
  }

  // Parse the date
  let date: Date;
  if (typeof value === 'string') {
    date = new Date(value);
  } else if (value instanceof Date) {
    date = value;
  } else if (typeof value === 'object' && value.__type === 'Date') {
    date = new Date(value.iso);
  } else {
    return <span className="text-white/30 italic">(invalid)</span>;
  }

  // Validate date
  if (isNaN(date.getTime())) {
    return <span className="text-white/30 italic">(invalid date)</span>;
  }

  // Format: "27 Nov 2025 15:44"
  const formatted = formatDateUTC(date);
  const isoString = date.toISOString();

  return (
    <span 
      className="text-white/70 cursor-help whitespace-nowrap"
      title={isoString}
    >
      {formatted}
    </span>
  );
}

/**
 * Format date in UTC: "27 Nov 2025 15:44"
 */
function formatDateUTC(date: Date): string {
  const day = date.getUTCDate();
  const month = MONTHS[date.getUTCMonth()];
  const year = date.getUTCFullYear();
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  
  return `${day} ${month} ${year} ${hours}:${minutes}`;
}

